import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { Btn } from "./common";
import { useApp } from "../context/AppContext";
import { isVisible, groupSections, calcAge, MultiSelect, ImageField, FamilyEditor } from "./ProfileFields";
import { GOV_TYPES, GovField, OriginSection } from "./GovFields";

const inputCls = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none disabled:bg-slate-100";

export function FieldInput({ f, value, onChange, disabled }) {
  const id = `field-${f.key}`;
  const common = { id, "data-testid": id, disabled, className: inputCls };
  let el;
  if (f.type === "textarea") el = <textarea {...common} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  else if (f.type === "select") el = (
    <select {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select…</option>
      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  else if (f.type === "bool") el = (
    <label className="flex items-center gap-2 text-sm py-2">
      <input type="checkbox" id={id} data-testid={id} disabled={disabled} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[var(--brand-primary)]" />
      {f.label}
    </label>
  );
  else el = (
    <input {...common} type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : f.type === "url" ? "url" : f.type === "phone" ? "tel" : "text"}
      step={f.type === "number" ? "any" : undefined} min={f.min} max={f.max} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
  );
  return (
    <div className={f.type === "textarea" ? "sm:col-span-2" : ""}>
      {f.type !== "bool" && (
        <label htmlFor={id} className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-700">
          {f.label}{f.required && <span className="text-red-600">*</span>}
          {disabled && <Lock className="h-3 w-3 text-slate-400" />}
        </label>
      )}
      {el}
    </div>
  );
}

export function FormBody({ module, data, setData, restricted, record, onBusy }) {
  const { meta } = useApp();
  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const sysValue = (f) => {
    if (f.key === "age") return calcAge(data.birth_date) ?? "—";
    if (f.key === "members_count" && data.head_resident_id) return 1 + (data.members?.length || 0);
    if (record?.data[f.key] != null) return Array.isArray(record.data[f.key]) ? record.data[f.key].join(", ") : String(record.data[f.key]);
    if (f.inherited) return meta?.tenant?.[f.key] || "—";
    return "Auto-generated";
  };
  const Label = ({ f }) => <p className="mb-1 text-xs font-medium text-slate-700">{f.label}{f.required && <span className="text-red-600">*</span>}</p>;
  return groupSections(module.fields).map(([section, fs]) => (
    <fieldset key={section} className="sm:col-span-2 grid sm:grid-cols-2 gap-4 rounded-md border p-4" data-testid={`form-section-${section[0]}`}>
      <legend className="px-1 text-sm font-semibold text-brand">{section}</legend>
      {fs.filter((f) => f.type !== "resident_ref" && isVisible(f, data)).map((f) => {
        if (f.type === "family") return <FamilyEditor key={f.key} module={module} record={record} data={data} setData={setData} />;
        if (f.system) return (
          <div key={f.key}><Label f={f} /><div className="rounded-md border border-dashed bg-slate-50 px-3 py-2 text-sm text-slate-600 flex items-center gap-1" data-testid={`readonly-${f.key}`}><Lock className="h-3 w-3" />{restricted.has(f.key) ? "Restricted" : sysValue(f)}</div></div>
        );
        if (f.type === "multiselect") return <div key={f.key} className="sm:col-span-2"><Label f={f} /><MultiSelect f={f} value={data[f.key]} onChange={set(f.key)} disabled={restricted.has(f.key)} /></div>;
        if (f.type === "image") return <div key={f.key}><Label f={f} />{restricted.has(f.key) ? <p className="text-sm text-slate-500">Restricted</p> : <ImageField f={f} value={data[f.key]} onChange={set(f.key)} module={module} record={record} onBusy={onBusy} />}</div>;
        return <FieldInput key={f.key} f={f} value={data[f.key]} disabled={restricted.has(f.key)} onChange={set(f.key)} />;
      })}
    </fieldset>
  ));
}

export default function RecordForm({ module, open, onClose, record, onSaved }) {
  const fields = module.fields.filter((f) => !f.system);
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dup, setDup] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setData(record ? { ...record.data } : {});
      setDup(null);
      setError("");
    }
  }, [open, record]);

  const restricted = new Set(record?.restricted_fields || (module.can_view_sensitive ? [] : fields.filter((f) => f.sensitive).map((f) => f.key)));

  const submit = async (confirm = false) => {
    const missing = fields.filter((f) => f.required && f.type !== "family" && !(f.type === "inhabitant" && data[f.display]) && !restricted.has(f.key) && isVisible(f, data) && (data[f.key] === undefined || data[f.key] === "" || data[f.key] === null || (Array.isArray(data[f.key]) && !data[f.key].length)));
    if (missing.length) { setError(`Please complete: ${missing.map((f) => f.label).join(", ")}`); return; }
    setBusy(true); setError("");
    const payload = Object.fromEntries(fields.filter((f) => !restricted.has(f.key) && f.key in data).map((f) => [f.key, data[f.key]]));
    try {
      const res = record
        ? await api.put(`/m/${module.key}/records/${record.id}`, { data: payload, version: record.version })
        : await api.post(`/m/${module.key}/records`, { data: payload, confirm_duplicate: confirm });
      toast.success(record ? "Record updated" : `Record ${res.data.ref_no} created`);
      onSaved(res.data);
      onClose();
    } catch (e) {
      if (e.response?.status === 409 && /duplicate/i.test(errMsg(e))) setDup(errMsg(e));
      else setError(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="record-form-dialog">
        <DialogHeader>
          <DialogTitle>{record ? `Edit ${record.ref_no}` : `New ${module.name} record`}</DialogTitle>
          <DialogDescription>Fields marked * are required. Changes are validated on the server and recorded in the audit trail.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="grid sm:grid-cols-2 gap-4" data-testid="record-form">
          {module.fields.some((f) => f.section) ? <FormBody module={module} data={data} setData={setData} restricted={restricted} record={record} onBusy={setUploading} /> : fields.filter((f) => !f.origin).map((f) => (GOV_TYPES.includes(f.type)
            ? <GovField key={f.key} f={f} module={module} data={data} setData={setData} record={record} onBusy={setUploading} />
            : <FieldInput key={f.key} f={f} value={data[f.key]} disabled={restricted.has(f.key)} onChange={(v) => setData((d) => ({ ...d, [f.key]: v }))} />
          ))}
          {module.origin_rules && <OriginSection module={module} data={data} setData={setData} record={record} onBusy={setUploading} />}
          {error && <p className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="record-form-error">{error}</p>}
          {dup && (
            <div className="sm:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="duplicate-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <div className="flex-1">{dup}
                <div className="mt-2"><Btn type="button" variant="warn" onClick={() => submit(true)} data-testid="confirm-duplicate-btn">Save anyway</Btn></div>
              </div>
            </div>
          )}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Btn type="button" variant="outline" onClick={onClose} data-testid="record-form-cancel">Cancel</Btn>
            <Btn type="submit" disabled={busy || uploading} data-testid="record-form-submit">{busy ? "Saving…" : uploading ? "Uploading file…" : "Save record"}</Btn>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
